import React, { useState, useEffect } from "react";
import { ref, onValue, update, push } from "firebase/database";
import { db, auth } from "../firebase";
import Popup from "./Popup";
import Confirm from "./Confirm";

const AuctionControl = ({ gameId }) => {
  const [status, setStatus] = useState("upcoming");
  const [loading, setLoading] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [players, setPlayers] = useState([]);
  const [bids, setBids] = useState([]);
  const [highestBid, setHighestBid] = useState(null);
  const [teams, setTeams] = useState([]);
  const [auctionSettings, setAuctionSettings] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [popup, setPopup] = useState({
    isOpen: false,
    message: "",
    title: "",
    type: "info",
  });
  const [confirm, setConfirm] = useState({
    isOpen: false,
    message: "",
    title: "",
    type: "warning",
    onConfirm: null,
  });
  const [showAssignTeam, setShowAssignTeam] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [assignPrice, setAssignPrice] = useState(0);

  useEffect(() => {
    // Check authentication and role
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const userRef = ref(db, `users/${user.uid}`);
          onValue(
            userRef,
            (snap) => {
              if (snap.exists()) {
                const userData = snap.val();
                setIsAuthenticated(userData.role === "organizer");
              } else {
                setIsAuthenticated(false);
              }
            },
            { onlyOnce: true }
          );
        } catch (error) {
          console.error("Error checking user role:", error);
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!gameId) return;

    // Listen to entire game data in one call
    const gameRef = ref(db, `games/${gameId}`);
    const unsubscribeGame = onValue(gameRef, async (snapshot) => {
      if (snapshot.exists()) {
        const gameData = snapshot.val();

        // Set status
        if (gameData.details?.status) {
          setStatus(gameData.details.status);
        }

        // Set auction settings
        if (gameData.auctionSettings) {
          setAuctionSettings(gameData.auctionSettings);
        }

        // Set players
        let playersArray = [];
        if (gameData.players) {
          playersArray = Object.entries(gameData.players).map(
            ([id, player]) => ({
              ...player,
              id,
            })
          );
          // Sort players by price descending (highest base price first)
          playersArray.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
          setPlayers(playersArray);
        } else {
          setPlayers([]);
        }

        // Set current player
        if (gameData.currentPlayer) {
          setCurrentPlayer(gameData.currentPlayer);
        } else {
          // If auction is live and no current player, auto-set first upcoming player
          if (gameData.details?.status === "live" && playersArray.length > 0) {
            const upcomingPlayers = playersArray.filter(
              (p) => p.status === "upcoming"
            );
            if (upcomingPlayers.length > 0) {
              const firstPlayer = upcomingPlayers[0];
              const playerDetail = {
                id: firstPlayer.id,
                ...firstPlayer,
                currentBid: firstPlayer.price,
                biddingStartTime: new Date().toISOString(),
              };
              setCurrentPlayer(playerDetail);
              try {
                await update(ref(db, `games/${gameId}`), {
                  currentPlayer: playerDetail,
                  currentBids: {},
                });
                // Log will be set by the listener on next update
              } catch (error) {
                console.error("Error setting first player:", error);
              }
            }
          }
        }

        // Set teams
        if (gameData.teams) {
          const teamsArray = Object.entries(gameData.teams).map(
            ([id, team]) => ({
              ...team,
              id,
            })
          );
          setTeams(teamsArray);
        } else {
          setTeams([]);
        }

        // Set bids
        if (gameData.currentBids) {
          const bidsArray = Object.entries(gameData.currentBids).map(
            ([id, bid]) => ({
              ...bid,
              id,
            })
          );
          // Sort by amount descending
          bidsArray.sort((a, b) => b.amount - a.amount);
          setBids(bidsArray);
          setHighestBid(bidsArray[0] || null);
        } else {
          setBids([]);
          setHighestBid(null);
        }
      }
    });

    return () => {
      unsubscribeGame();
    };
  }, [gameId]);

  // Helper function to add log entry
  const addLog = async (action, details) => {
    try {
      const logRef = ref(db, `games/${gameId}/logs`);
      await push(logRef, {
        action,
        details,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error adding log:", error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await update(ref(db, `games/${gameId}/details`), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
      setPopup({
        isOpen: true,
        title: "Status Updated",
        message: `Auction status changed to: ${newStatus}`,
        type: "success",
      });
    } catch (error) {
      console.error("Error changing status:", error);
      setPopup({
        isOpen: true,
        title: "Error",
        message: "Failed to change status: " + error.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartAuction = async () => {
    if (!auctionSettings) {
      setPopup({
        isOpen: true,
        title: "Configuration Required",
        message: "Please configure auction settings first!",
        type: "warning",
      });
      return;
    }

    // Get first upcoming player
    const upcomingPlayers = players.filter((p) => p.status === "upcoming");
    if (upcomingPlayers.length === 0) {
      setPopup({
        isOpen: true,
        title: "No Players Available",
        message: "No upcoming players to auction!",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const firstPlayer = upcomingPlayers[0];

      // Set first player as current
      await update(ref(db, `games/${gameId}`), {
        currentPlayer: {
          id: firstPlayer.id,
          ...firstPlayer,
          currentBid: firstPlayer.price,
          biddingStartTime: new Date().toISOString(),
        },
        "details/status": "live",
        currentBids: {},
      });

      // Log auction start
      await addLog("AUCTION_STARTED", {
        playerName: firstPlayer.name,
        playerId: firstPlayer.id,
        basePrice: firstPlayer.price,
      });

      setPopup({
        isOpen: true,
        title: "Auction Started",
        message: "Auction started!",
        type: "success",
      });
    } catch (error) {
      console.error("Error starting auction:", error);
      setPopup({
        isOpen: true,
        title: "Error",
        message: "Failed to start auction: " + error.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNextPlayer = async () => {
    if (!currentPlayer) {
      setPopup({
        isOpen: true,
        title: "No Current Player",
        message: "No current player!",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      // If there are bids, assign player to highest bidder
      if (highestBid) {
        const winningTeam = teams.find((t) => t.id === highestBid.teamId);
        if (!winningTeam) {
          throw new Error("Winning team not found!");
        }

        // Check if team has enough purse
        if (winningTeam.purseLeft < highestBid.amount) {
          setLoading(false);
          setPopup({
            isOpen: true,
            title: "Insufficient Funds",
            message: "Team doesn't have enough purse!",
            type: "error",
          });
          return;
        }

        // Update player status
        await update(ref(db, `games/${gameId}/players/${currentPlayer.id}`), {
          status: "sold",
          soldPrice: highestBid.amount,
          teamId: highestBid.teamId,
          teamName: highestBid.teamName,
          soldAt: new Date().toISOString(),
        });

        // Update team
        const newPurse = winningTeam.purseLeft - highestBid.amount;
        await update(ref(db, `games/${gameId}/teams/${highestBid.teamId}`), {
          purseLeft: newPurse,
          [`players/${currentPlayer.id}`]: {
            name: currentPlayer.name,
            price: highestBid.amount,
            role: currentPlayer.role,
            time: new Date().toISOString(),
          },
        });

        // Log player sold
        await addLog("PLAYER_SOLD", {
          playerName: currentPlayer.name,
          playerId: currentPlayer.id,
          teamName: highestBid.teamName,
          teamId: highestBid.teamId,
          soldPrice: highestBid.amount,
          basePrice: currentPlayer.price,
        });
      } else {
        // Mark as unsold
        await update(ref(db, `games/${gameId}/players/${currentPlayer.id}`), {
          status: "unsold",
        });

        // Log player unsold
        await addLog("PLAYER_UNSOLD", {
          playerName: currentPlayer.name,
          playerId: currentPlayer.id,
          basePrice: currentPlayer.price,
        });
      }

      // Get next upcoming player
      const upcomingPlayers = players.filter(
        (p) => p.status === "upcoming" && p.id !== currentPlayer.id
      );

      if (upcomingPlayers.length === 0) {
        // Auction completed
        await update(ref(db, `games/${gameId}`), {
          currentPlayer: null,
          currentBids: null,
          "details/status": "completed",
        });

        // Log auction completion
        await addLog("AUCTION_COMPLETED", {
          totalPlayers: players.length,
          soldCount: players.filter((p) => p.status === "sold").length,
          unsoldCount: players.filter((p) => p.status === "unsold").length,
        });

        setPopup({
          isOpen: true,
          title: "Auction Completed",
          message: "Auction completed! No more players.",
          type: "success",
        });
      } else {
        // Set next player
        const nextPlayer = upcomingPlayers[0];
        await update(ref(db, `games/${gameId}`), {
          currentPlayer: {
            id: nextPlayer.id,
            ...nextPlayer,
            currentBid: nextPlayer.price,
            biddingStartTime: new Date().toISOString(),
          },
          currentBids: {},
        });

        // Log next player
        await addLog("NEXT_PLAYER", {
          playerName: nextPlayer.name,
          playerId: nextPlayer.id,
          basePrice: nextPlayer.price,
        });
      }
    } catch (error) {
      console.error("Error moving to next player:", error);
      setPopup({
        isOpen: true,
        title: "Error",
        message: "Failed to move to next player: " + error.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPlayer = async () => {
    if (!currentPlayer) return;

    setLoading(true);
    try {
      // Mark as unsold
      await update(ref(db, `games/${gameId}/players/${currentPlayer.id}`), {
        status: "unsold",
      });

      // Log player skipped
      await addLog("PLAYER_SKIPPED", {
        playerName: currentPlayer.name,
        playerId: currentPlayer.id,
        basePrice: currentPlayer.price,
      });

      // Get next player
      const upcomingPlayers = players.filter(
        (p) => p.status === "upcoming" && p.id !== currentPlayer.id
      );

      if (upcomingPlayers.length === 0) {
        await update(ref(db, `games/${gameId}`), {
          currentPlayer: null,
          currentBids: null,
          "details/status": "completed",
        });
        setPopup({
          isOpen: true,
          title: "No More Players",
          message: "No more players!",
          type: "info",
        });
      } else {
        const nextPlayer = upcomingPlayers[0];
        await update(ref(db, `games/${gameId}`), {
          currentPlayer: {
            id: nextPlayer.id,
            ...nextPlayer,
            currentBid: nextPlayer.price,
            biddingStartTime: new Date().toISOString(),
          },
          currentBids: {},
        });
      }
    } catch (error) {
      console.error("Error skipping player:", error);
      setPopup({
        isOpen: true,
        title: "Error",
        message: "Failed to skip player: " + error.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearBids = async () => {
    if (!currentPlayer) return;

    setConfirm({
      isOpen: true,
      title: "Clear All Bids",
      message: "Clear all bids for current player?",
      type: "warning",
      onConfirm: async () => {
        try {
          await update(ref(db, `games/${gameId}`), {
            currentBids: null,
          });
          await addLog("BIDS_CLEARED", {
            playerId: currentPlayer.id,
            playerName: currentPlayer.name,
          });
          setPopup({
            isOpen: true,
            title: "Bids Cleared",
            message: "Bids cleared!",
            type: "success",
          });
        } catch (error) {
          console.error("Error clearing bids:", error);
          setPopup({
            isOpen: true,
            title: "Error",
            message: "Failed to clear bids: " + error.message,
            type: "error",
          });
        }
        setConfirm({ isOpen: false, message: "", title: "", onConfirm: null });
      },
    });
  };

  const handleManualAssign = async () => {
    if (!selectedTeamId || !assignPrice) {
      setPopup({
        isOpen: true,
        title: "Missing Information",
        message: "Please select a team and enter a valid price!",
        type: "warning",
      });
      return;
    }

    const selectedTeam = teams.find((t) => t.id === selectedTeamId);
    if (!selectedTeam) {
      setPopup({
        isOpen: true,
        title: "Error",
        message: "Selected team not found!",
        type: "error",
      });
      return;
    }

    if (Number(selectedTeam.purseLeft) < assignPrice) {
      setPopup({
        isOpen: true,
        title: "Insufficient Funds",
        message: `${
          selectedTeam.teamName
        } doesn't have enough purse! Available: ৳${selectedTeam.purseLeft.toLocaleString()}`,
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      // Update player status
      await update(ref(db, `games/${gameId}/players/${currentPlayer.id}`), {
        status: "sold",
        soldPrice: assignPrice,
        teamId: selectedTeamId,
        teamName: selectedTeam.teamName,
        soldAt: new Date().toISOString(),
      });

      // Update team
      const newPurse = Number(selectedTeam.purseLeft) - assignPrice;
      await update(ref(db, `games/${gameId}/teams/${selectedTeamId}`), {
        purseLeft: newPurse,
        [`players/${currentPlayer.id}`]: {
          name: currentPlayer.name,
          price: assignPrice,
          role: currentPlayer.role,
          time: new Date().toISOString(),
        },
      });

      // Log manual assignment
      await addLog("PLAYER_MANUALLY_ASSIGNED", {
        playerName: currentPlayer.name,
        playerId: currentPlayer.id,
        teamName: selectedTeam.teamName,
        teamId: selectedTeamId,
        soldPrice: assignPrice,
        basePrice: currentPlayer.price,
      });

      // Get next player
      const upcomingPlayers = players.filter(
        (p) => p.status === "upcoming" && p.id !== currentPlayer.id
      );

      if (upcomingPlayers.length === 0) {
        // Auction completed
        await update(ref(db, `games/${gameId}`), {
          currentPlayer: null,
          currentBids: null,
          "details/status": "completed",
        });

        await addLog("AUCTION_COMPLETED", {
          totalPlayers: players.length,
          soldCount: players.filter((p) => p.status === "sold").length,
          unsoldCount: players.filter((p) => p.status === "unsold").length,
        });

        setPopup({
          isOpen: true,
          title: "Auction Completed",
          message: "Player assigned successfully! Auction completed.",
          type: "success",
        });
      } else {
        // Set next player
        const nextPlayer = upcomingPlayers[0];
        await update(ref(db, `games/${gameId}`), {
          currentPlayer: {
            id: nextPlayer.id,
            ...nextPlayer,
            currentBid: nextPlayer.price,
            biddingStartTime: new Date().toISOString(),
          },
          currentBids: {},
        });

        await addLog("NEXT_PLAYER", {
          playerName: nextPlayer.name,
          playerId: nextPlayer.id,
          basePrice: nextPlayer.price,
        });

        setPopup({
          isOpen: true,
          title: "Success",
          message: `${currentPlayer.name} assigned to ${
            selectedTeam.teamName
          } for ৳${assignPrice.toLocaleString()}!`,
          type: "success",
        });
      }

      // Reset and close popup
      setShowAssignTeam(false);
      setSelectedTeamId("");
      setAssignPrice(0);
    } catch (error) {
      console.error("Error manually assigning player:", error);
      setPopup({
        isOpen: true,
        title: "Error",
        message: "Failed to assign player: " + error.message,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReauctionUnsold = async () => {
    const unsoldPlayers = players.filter((p) => p.status === "unsold");

    if (unsoldPlayers.length === 0) {
      setPopup({
        isOpen: true,
        title: "No Unsold Players",
        message: "No unsold players to re-auction!",
        type: "warning",
      });
      return;
    }

    setConfirm({
      isOpen: true,
      title: "Re-auction Unsold Players",
      message: `Re-auction ${unsoldPlayers.length} unsold player(s)? They will be set back to upcoming status.`,
      type: "warning",
      onConfirm: async () => {
        setLoading(true);
        try {
          const updates = {};
          unsoldPlayers.forEach((player) => {
            updates[`games/${gameId}/players/${player.id}/status`] = "upcoming";
            updates[`games/${gameId}/players/${player.id}/teamId`] = null;
            updates[`games/${gameId}/players/${player.id}/soldPrice`] = null;
          });

          await update(ref(db), updates);
          setPopup({
            isOpen: true,
            title: "Re-auction Complete",
            message: `${unsoldPlayers.length} unsold player(s) have been set back to upcoming status!`,
            type: "success",
          });
        } catch (error) {
          console.error("Error re-auctioning unsold players:", error);
          setPopup({
            isOpen: true,
            title: "Error",
            message: "Failed to re-auction unsold players: " + error.message,
            type: "error",
          });
        } finally {
          setLoading(false);
        }
        setConfirm({ isOpen: false, message: "", title: "", onConfirm: null });
      },
    });
  };

  const upcomingCount = players.filter((p) => p.status === "upcoming").length;
  const soldCount = players.filter((p) => p.status === "sold").length;
  const unsoldCount = players.filter((p) => p.status === "unsold").length;

  return (
    <div className="card shadow">
      <div className="card-body p-4">
        <h3 className="mb-4">
          <i className="bi bi-gear-fill me-2"></i>
          {isAuthenticated ? "Auction Control Panel" : "Live Auction"}
        </h3>

        {/* Status Controls */}
        <div className="mb-4">
          <h5 className="mb-3">
            <i className="bi bi-toggle-on me-2"></i>
            Auction Status
          </h5>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <button
              className={`btn ${
                status === "upcoming" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => handleStatusChange("upcoming")}
              disabled={loading || !isAuthenticated}
            >
              <i className="bi bi-clock-fill me-2"></i>
              Upcoming
            </button>
            <button
              className={`btn ${
                status === "live" ? "btn-success" : "btn-outline-success"
              }`}
              onClick={() => handleStatusChange("live")}
              disabled={loading || !isAuthenticated}
            >
              <i className="bi bi-broadcast me-2"></i>
              Live
            </button>
            <button
              className={`btn ${
                status === "paused" ? "btn-warning" : "btn-outline-warning"
              }`}
              onClick={() => handleStatusChange("paused")}
              disabled={loading || !isAuthenticated}
            >
              <i className="bi bi-pause-circle-fill me-2"></i>
              Paused
            </button>
            <button
              className={`btn ${
                status === "completed"
                  ? "btn-secondary"
                  : "btn-outline-secondary"
              }`}
              onClick={() => handleStatusChange("completed")}
              disabled={loading || !isAuthenticated}
            >
              <i className="bi bi-check-circle-fill me-2"></i>
              Completed
            </button>
            {auctionSettings?.unsoldAction !== "Remove from auction" &&
              isAuthenticated &&
              unsoldCount > 0 && (
                <button
                  className="btn btn-warning "
                  onClick={handleReauctionUnsold}
                  disabled={loading || !isAuthenticated}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Re-auction Unsold Players ({unsoldCount})
                    </>
                  )}
                </button>
              )}
          </div>

        </div>

        {/* Player Stats — compact strip */}
        <div className="d-flex flex-wrap gap-2 mb-4">
          <span className="badge rounded-pill bg-secondary px-3 py-2" style={{ fontSize: "0.8rem" }}>
            <i className="bi bi-clock-history me-1"></i>
            Upcoming <strong className="ms-1">{upcomingCount}</strong>
          </span>
          <span className="badge rounded-pill bg-success px-3 py-2" style={{ fontSize: "0.8rem" }}>
            <i className="bi bi-check-circle me-1"></i>
            Sold <strong className="ms-1">{soldCount}</strong>
          </span>
          <span className="badge rounded-pill bg-danger px-3 py-2" style={{ fontSize: "0.8rem" }}>
            <i className="bi bi-x-circle me-1"></i>
            Unsold <strong className="ms-1">{unsoldCount}</strong>
          </span>
          <span className="badge rounded-pill bg-primary px-3 py-2" style={{ fontSize: "0.8rem" }}>
            <i className="bi bi-people me-1"></i>
            Total <strong className="ms-1">{players.length}</strong>
          </span>
        </div>

        {/* Auction Actions */}
        {status === "upcoming" && (
          <div className="mb-4">
            <button
              className="btn btn-success btn-lg w-100 mb-3"
              onClick={handleStartAuction}
              disabled={loading || upcomingCount === 0 || !isAuthenticated}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Starting...
                </>
              ) : (
                <>
                  <i className="bi bi-play-circle-fill me-2"></i>
                  Start Auction
                </>
              )}
            </button>
            {upcomingCount === 0 && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                No players available to auction. Please add players first.
              </div>
            )}
          </div>
        )}

        {status === "live" && (
          <div>
            {/* Current Player Info — hero card */}
            {currentPlayer && (
              <div className="card border-0 mb-4 shadow-sm overflow-hidden" style={{ background: "linear-gradient(135deg, #0d6efd11 0%, #0d6efd22 100%)", border: "2px solid #0d6efd" }}>
                <div className="card-header bg-primary text-white d-flex align-items-center gap-2 py-2">
                  <i className="bi bi-person-circle fs-5"></i>
                  <span className="fw-semibold">Current Player on Auction</span>
                  {currentPlayer.playerNumber && (
                    <span className="badge bg-white text-primary ms-auto">#{currentPlayer.playerNumber}</span>
                  )}
                </div>
                <div className="card-body p-3">
                  <div className="d-flex align-items-center gap-4">
                    {/* Left: Large Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={currentPlayer.image || "/player.png"}
                        alt={currentPlayer.name}
                        className="rounded shadow"
                        style={{
                          width: "350px",
                          height: "350px",
                          objectFit: "cover",
                          border: "4px solid #0d6efd",
                        }}
                      />
                    </div>
                    {/* Right: Details */}
                    <div className="flex-grow-1">
                      <h2 className="fw-bold mb-2">{currentPlayer.name}</h2>
                      <div className="mb-3">
                        <span className="badge bg-info fs-6 px-3 py-2">{currentPlayer.role}</span>
                      </div>
                      <div className="d-flex flex-wrap gap-4">
                        <div>
                          <div className="text-muted small fw-semibold text-uppercase" style={{ letterSpacing: "0.05em" }}>Base Price</div>
                          <div className="fs-4 fw-bold text-success">৳{currentPlayer.price?.toLocaleString()}</div>
                        </div>
                        {currentPlayer.playerNumber && (
                          <div>
                            <div className="text-muted small fw-semibold text-uppercase" style={{ letterSpacing: "0.05em" }}>Jersey No.</div>
                            <div className="fs-4 fw-bold text-primary">#{currentPlayer.playerNumber}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Current Bids */}
            <div className="card mb-4">
              <div className="card-header bg-success text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-currency-dollar me-2"></i>
                    Current Bids ({bids.length})
                  </h5>
                  {isAuthenticated && bids.length > 0 && (
                    <button
                      className="btn btn-sm btn-light"
                      onClick={handleClearBids}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Clear Bids
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body">
                {bids.length === 0 ? (
                  <div className="alert alert-warning mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    No bids yet. Waiting for bidders...
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Team</th>
                          <th>Amount</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bids.map((bid, index) => (
                          <tr
                            key={bid.id}
                            className={index === 0 ? "table-success" : ""}
                          >
                            <td>
                              {index === 0 ? (
                                <i className="bi bi-trophy-fill text-warning fs-5"></i>
                              ) : (
                                index + 1
                              )}
                            </td>
                            <td>
                              <strong>{bid.teamName}</strong>
                            </td>
                            <td className="fw-bold text-success">
                              ৳{bid.amount.toLocaleString()}
                            </td>
                            <td>
                              {new Date(bid.timestamp).toLocaleTimeString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {highestBid && (
                  <div className="alert alert-success mt-3 mb-0">
                    <strong>
                      <i className="bi bi-trophy-fill me-2"></i>
                      Highest Bid:
                    </strong>{" "}
                    {highestBid.teamName} - ৳
                    {highestBid.amount.toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {isAuthenticated && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-primary flex-fill"
                  onClick={handleNextPlayer}
                  disabled={loading || !currentPlayer}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-right-circle-fill me-1"></i>
                      {highestBid
                        ? "Confirm & Next"
                        : "Mark Unsold & Next"}
                    </>
                  )}
                </button>

                <button
                  className="btn btn-sm btn-warning"
                  onClick={handleSkipPlayer}
                  disabled={loading || !currentPlayer}
                >
                  <i className="bi bi-skip-forward-fill me-1"></i>
                  Skip
                </button>

                <button
                  className="btn btn-sm btn-info"
                  onClick={() => {
                    setShowAssignTeam(true);
                    setAssignPrice(currentPlayer?.price || 0);
                  }}
                  disabled={loading || !currentPlayer}
                >
                  <i className="bi bi-people-fill me-1"></i>
                  Assign
                </button>
              </div>
            )}
          </div>
        )}

        {status === "paused" && (
          <div className="alert alert-warning">
            <i className="bi bi-pause-circle-fill me-2"></i>
            Auction is paused. Change status to "Live" to continue.
          </div>
        )}

        {status === "completed" && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle-fill me-2"></i>
            Auction has been completed!
          </div>
        )}
      </div>

      <Popup
        isOpen={popup.isOpen}
        onClose={() =>
          setPopup({ isOpen: false, message: "", title: "", type: "info" })
        }
        message={popup.message}
        title={popup.title}
        type={popup.type}
      />

      <Confirm
        isOpen={confirm.isOpen}
        onConfirm={confirm.onConfirm}
        onCancel={() =>
          setConfirm({ isOpen: false, message: "", title: "", onConfirm: null })
        }
        message={confirm.message}
        title={confirm.title}
        type={confirm.type}
      />

      {/* Manual Team Assignment Modal */}
      {showAssignTeam && currentPlayer && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <i className="bi bi-people-fill me-2"></i>
                  Manually Assign Player to Team
                </h5>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowAssignTeam(false);
                    setSelectedTeamId("");
                    setAssignPrice(0);
                  }}
                  disabled={loading}
                ></button>
              </div>

              <div className="modal-body">
                {/* Player Details */}
                <div className="card mb-3 border-primary">
                  <div className="card-header bg-primary bg-opacity-10">
                    <strong>
                      <i className="bi bi-person-circle me-2"></i>
                      Player Details
                    </strong>
                  </div>
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-4 text-center">
                        <img
                          src={currentPlayer.image || "/player.png"}
                          alt={currentPlayer.name}
                          className="img-fluid rounded-circle"
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            border: "3px solid #0d6efd",
                          }}
                        />
                      </div>
                      <div className="col-8">
                        <h5 className="mb-1">{currentPlayer.name}</h5>
                        <p className="mb-1">
                          <span className="badge bg-info">
                            {currentPlayer.role}
                          </span>
                        </p>
                        <p className="mb-0">
                          <strong>Base Price:</strong> ৳
                          {currentPlayer.price?.toLocaleString()}
                        </p>
                        {currentPlayer.playerNumber && (
                          <p className="mb-0">
                            <strong>Number:</strong> #
                            {currentPlayer.playerNumber}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Team Selection */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-shield-fill me-2"></i>
                    Select Team *
                  </label>
                  <select
                    className="form-select form-select-lg"
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">-- Choose a Team --</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.teamName} (Purse Left: ৳
                        {team.purseLeft?.toLocaleString() || 0})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price Input */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-currency-dollar me-2"></i>
                    Sold Price (৳) *
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-lg"
                    value={assignPrice}
                    onChange={(e) => setAssignPrice(Number(e.target.value))}
                    min={currentPlayer.price}
                    disabled={loading}
                    placeholder="Enter sold price"
                  />
                  <small className="text-muted">
                    Minimum: ৳{currentPlayer.price?.toLocaleString()}
                  </small>
                </div>

                {/* Selected Team Info */}
                {selectedTeamId && (
                  <div className="alert alert-info">
                    <strong>
                      <i className="bi bi-info-circle me-2"></i>
                      Selected Team:
                    </strong>{" "}
                    {teams.find((t) => t.id === selectedTeamId)?.teamName}
                    <br />
                    <strong>Available Purse:</strong> ৳
                    {teams
                      .find((t) => t.id === selectedTeamId)
                      ?.purseLeft?.toLocaleString()}
                    <br />
                    {assignPrice > 0 && (
                      <>
                        <strong>Remaining After:</strong> ৳
                        {(
                          teams.find((t) => t.id === selectedTeamId)
                            ?.purseLeft - assignPrice
                        )?.toLocaleString()}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAssignTeam(false);
                    setSelectedTeamId("");
                    setAssignPrice(0);
                  }}
                  disabled={loading}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleManualAssign}
                  disabled={
                    loading ||
                    !selectedTeamId ||
                    !assignPrice ||
                    assignPrice < currentPlayer.price
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Assigning...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle-fill me-1"></i>
                      Assign & Next Player
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionControl;
